import clsx from 'clsx'
import { GetStaticPropsContext } from 'next'
import Link from 'next/link'
import { useState } from 'react'
import { ImageType, MagazineHeroBlock } from '../../@types/statamic'
import MagazineQuery from '../../graphql/MagazineQuery.graphql'
import { PageParams, PreviewData } from '@/src/@types/infrastructur'
import {
  RadioGroupItem,
  RadioGroupRoot,
} from '@/src/components/core/RadioGroup'
import Content from '@/src/components/elements/Content'
import Picture from '@/src/components/elements/Picture'
import Title from '@/src/components/elements/Title'
import Container from '@/src/components/layout/Container'
import { request, statamicConfig } from '@/src/StatamicGraphQLClient'

export function getTitle({ title }: MagazineHeroBlock) {
  return title
}

export function getDescription({ text }: MagazineHeroBlock) {
  if (text) {
    const pTag = text.match(/.*?<p>(.*?)<\/p>/)
    if (pTag) {
      return pTag[1]
    }
  }
  return undefined
}

export async function getStaticProps(
  { category, ...props }: MagazineHeroBlock,
  page: GetStaticPropsContext<PageParams, PreviewData>
) {
  const {
    entries: { data: entries },
  } = await request<{
    entries: {
      data: {
        uri: string
        title: string
        image?: ImageType
        preview_text?: string
        date: string
        isoDate: string
        tags: string[]
        reading_time?: number
      }[]
    }
  }>(
    MagazineQuery,
    {
      locale: `${process.env.NEXT_PUBLIC_STATAMIC_SITE_HANDLE}_de`,
      category: category ? category.value : 'magazine',
      collection: statamicConfig.magazineCollection,
    },
    page.previewData?.statamicToken
  )

  return {
    ...props,
    entries,
  }
}

export const SORTED_MAGAZINE_TAGS = [
  'Auto',
  'Motorrad',
  'Nothelferkurs',
  'VKU',
  'Tipps & Tricks',
  'Prüfungsvorbereitung',
  'BLINK Vorteile',
  'News',
  'App',
]

export default function MagazineHero({
  title,
  text,
  entries,
}: Awaited<ReturnType<typeof getStaticProps>>) {
  const tags = [
    ...new Set(
      entries
        .flatMap((entry) => entry.tags)
        .filter(Boolean)
        .sort(
          (a, b) =>
            SORTED_MAGAZINE_TAGS.indexOf(a) - SORTED_MAGAZINE_TAGS.indexOf(b)
        )
    ),
  ]

  const [selectedTag, setSelectedTag] = useState('ALL')

  const filteredEntries = entries.filter(
    (entry) =>
      selectedTag === 'ALL' || entry.tags?.some((tag) => tag === selectedTag)
  )

  const latestEntry = entries[0]

  return (
    <div>
      <Container>
        <div>
          <Title style="bigH1">{title}</Title>
          <div className="mt-4 md:mt-6">
            <Content content={text} />
          </div>
          <div className="mt-16 hidden lg:block">
            <article>
              <Link
                href={latestEntry.uri}
                className="group grid grid-cols-2 gap-8"
              >
                {latestEntry.image && (
                  <div className="relative aspect-16/9 w-full overflow-hidden rounded-xl border border-gray-150">
                    <Picture
                      {...latestEntry.image}
                      sizes="800px"
                      fill
                      className="object-cover transition-transform duration-300 group-hover:scale-105"
                    />
                    {latestEntry.reading_time && (
                      <p className="absolute top-2 right-2 block rounded bg-gray-800 px-2 py-1 text-sm text-white shadow-sm">
                        {latestEntry.reading_time} Min.
                      </p>
                    )}
                  </div>
                )}
                <div className="flex flex-col justify-center">
                  {latestEntry.tags && (
                    <div className="flex flex-wrap">
                      {latestEntry.tags
                        .sort(
                          (a, b) =>
                            SORTED_MAGAZINE_TAGS.indexOf(a) -
                            SORTED_MAGAZINE_TAGS.indexOf(b)
                        )
                        .map((tag, index) => (
                          <>
                            <p key={tag} className="text-sm text-gray-600">
                              {tag}{' '}
                            </p>
                            {index < latestEntry.tags!.length - 1 && (
                              <p className="mx-1 text-sm text-gray-600">·</p>
                            )}
                          </>
                        ))}
                    </div>
                  )}
                  <h3 className="mt-1 text-7xl font-black group-hover:underline">
                    {latestEntry.title}
                  </h3>
                  <p className="mt-3 text-gray-800">
                    {latestEntry.preview_text}
                  </p>
                </div>
              </Link>
            </article>
          </div>
          <RadioGroupRoot
            value={selectedTag}
            onValueChange={setSelectedTag}
            className="mt-10 -ml-2 flex flex-wrap lg:mt-16"
          >
            <RadioGroupItem
              value={'ALL'}
              size="filterTag"
              color="blue-outlined"
              showCheckbox={false}
              className="mt-2 ml-2"
            >
              Alle
            </RadioGroupItem>
            {tags.map((tag) => (
              <RadioGroupItem
                key={tag}
                value={tag}
                size="filterTag"
                color="blue-outlined"
                showCheckbox={false}
                className="mt-2 ml-2"
              >
                {tag}
              </RadioGroupItem>
            ))}
          </RadioGroupRoot>
          <div className="mt-10 grid grid-cols-1 gap-10 md:grid-cols-2 md:gap-x-8 md:gap-y-16 lg:grid-cols-3">
            {filteredEntries.map((entry) => (
              <article
                key={entry.uri}
                className={clsx({
                  'lg:hidden': entry.uri === latestEntry.uri,
                })}
              >
                <Link href={entry.uri} className="group">
                  {entry.image && (
                    <div className="relative aspect-16/9 w-full overflow-hidden rounded-xl border border-gray-150">
                      <Picture
                        {...entry.image}
                        sizes="800px"
                        fill
                        className="object-cover transition-transform duration-300 group-hover:scale-105"
                      />
                      {entry.reading_time && (
                        <p className="absolute top-2 right-2 block rounded bg-gray-800 px-2 py-1 text-sm text-white shadow-sm">
                          {entry.reading_time} Min.
                        </p>
                      )}
                    </div>
                  )}
                  <div className="mt-4 flex flex-col justify-center">
                    {entry.tags && (
                      <div className="flex flex-wrap">
                        {entry.tags
                          .sort(
                            (a, b) =>
                              SORTED_MAGAZINE_TAGS.indexOf(a) -
                              SORTED_MAGAZINE_TAGS.indexOf(b)
                          )
                          .map((tag, index) => (
                            <>
                              <p key={tag} className="text-sm text-gray-600">
                                {tag}{' '}
                              </p>
                              {index < entry.tags!.length - 1 && (
                                <p className="mx-1 text-sm text-gray-600">·</p>
                              )}
                            </>
                          ))}
                      </div>
                    )}
                    <h3 className="mt-1 text-xl font-black group-hover:underline md:text-2xl">
                      {entry.title}
                    </h3>
                    <p className="mt-3 text-gray-800">{entry.preview_text}</p>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </div>
      </Container>
    </div>
  )
}
